import React from 'react';
import { useTranslation } from 'react-i18next';
import './ContactPage.css'; // We'll create this for styling

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-surface text-on-surface font-body selection:bg-secondary-container selection:text-on-secondary-container fade-in">
      <main className="relative">
        {/* Hero Section / Editorial Header */}
        <section className="relative h-[300px] md:h-[409px] min-h-[250px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-primary">
            <img className="w-full h-full object-cover opacity-60 mix-blend-overlay" alt="luxury seaside terrace overlooking the Adriatic sea at dusk" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBSH52bHPIUgEwSSpn1AiVpETTUygNgZVbko35qBeqyloPl6JgGIZJVudelBTbeef5QdBgweGpXAolSTxqC5D9WlEEtu3OjI4ccaOPaqy2U7L5mw7rjLLeXttxY34vwN0hr1tglVNYPv6oejBmjgKL51roQSMdhDiLikEifUzx_p7Mj59lsv4EO6nJeDhPLmrXCCxzZr0P_SC0xrH_YmfI-OnMsX9EeyrH51voXfZ6twiNwpw8fK_4NkueyNwlJQ8VIJWaO9Q34PUY" />
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-headline text-3xl sm:text-5xl md:text-7xl text-white font-light tracking-tight mb-4">{t('contactPage.title') || 'Get in Touch'}</h1>
            <p className="font-body text-white/90 text-lg max-w-xl mx-auto font-light">{t('contactPage.intro') || 'Experience the serenity of the Mediterranean. We are here to help you plan your perfect coastal getaway.'}</p>
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-12 md:-mt-20 relative z-20 pb-12 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Contact Form Card */}
            <div className="lg:col-span-7 bg-surface-container-lowest p-8 md:p-12 rounded-xl shadow-[0_10px_40px_rgba(27,28,25,0.05)]">
              <div className="mb-10">
                <span className="font-label text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-2 block">{t('contactPage.inquiryLabel')}</span>
                <h2 className="font-headline text-3xl text-primary font-medium">{t('contactPage.formTitle')}</h2>
              </div>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant font-bold ml-1">{t('finalizeBookingPage.form.firstName')} {t('finalizeBookingPage.form.lastName')}</label>
                    <input className="w-full bg-surface-container-low border-none border-b border-outline-variant/20 focus:ring-0 focus:bg-surface-container-high rounded-lg p-4 transition-all placeholder:text-outline/50" placeholder={t('finalizeBookingPage.form.placeholder.firstName')} type="text" />
                  </div>
                  <div className="space-y-1">
                    <label className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant font-bold ml-1">{t('finalizeBookingPage.form.email')}</label>
                    <input className="w-full bg-surface-container-low border-none border-b border-outline-variant/20 focus:ring-0 focus:bg-surface-container-high rounded-lg p-4 transition-all placeholder:text-outline/50" placeholder={t('finalizeBookingPage.form.placeholder.email')} type="email" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant font-bold ml-1">{t('contactPage.form.subject')}</label>
                  <input className="w-full bg-surface-container-low border-none border-b border-outline-variant/20 focus:ring-0 focus:bg-surface-container-high rounded-lg p-4 transition-all placeholder:text-outline/50" placeholder={t('contactPage.form.placeholder.subject')} type="text" />
                </div>
                <div className="space-y-1">
                  <label className="font-label text-[10px] uppercase tracking-wider text-on-surface-variant font-bold ml-1">{t('contactPage.form.message')}</label>
                  <textarea className="w-full bg-surface-container-low border-none border-b border-outline-variant/20 focus:ring-0 focus:bg-surface-container-high rounded-lg p-4 transition-all placeholder:text-outline/50" placeholder={t('contactPage.form.placeholder.message')} rows={5}></textarea>
                </div>
                <div className="pt-4">
                  <button className="bg-gradient-to-br from-[#002045] to-[#1a365d] text-white w-full md:w-auto px-12 py-4 rounded-xl font-label text-sm font-bold uppercase tracking-widest hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all" type="submit" onClick={(e) => e.preventDefault()}>
                    {t('contactPage.form.submit')}
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="lg:col-span-5 space-y-8">
              {/* Info Card */}
              <div className="bg-primary text-white p-10 rounded-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-primary-container rounded-full opacity-20 blur-3xl"></div>
                <h3 className="font-headline text-2xl mb-8 relative z-10">{t('contactPage.infoTitle')}</h3>
                <ul className="space-y-8 relative z-10">
                  <li className="flex items-start gap-4">
                    <span className="material-symbols-outlined material-symbols-rounded text-secondary-fixed text-2xl block leading-none">location_on</span>
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-widest text-on-primary-container font-bold mb-1">{t('contactPage.info.locationLabel')}</p>
                      <p className="font-body text-primary-fixed leading-relaxed">{t('contactPage.info.address')}</p>
                      <p className="font-body text-primary-fixed leading-relaxed">{t('contactPage.info.location')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="material-symbols-outlined material-symbols-rounded text-secondary-fixed text-2xl block leading-none">call</span>
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-widest text-on-primary-container font-bold mb-1">{t('finalizeBookingPage.form.phone')}</p>
                      <a href="tel:+38598330641" className="font-body text-primary-fixed hover:text-white transition-colors">+385 98 330 641</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="material-symbols-outlined material-symbols-rounded text-secondary-fixed text-2xl block leading-none">mail</span>
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-widest text-on-primary-container font-bold mb-1">{t('finalizeBookingPage.form.email')}</p>
                      <a href="mailto:argos@hotmail.com" className="font-body text-primary-fixed hover:text-white transition-colors">argos@hotmail.com</a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Interactive Map */}
              <div className="bg-surface-variant aspect-video rounded-xl overflow-hidden shadow-sm relative group">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1437.9156085800073!2d15.5430228!3d43.8800485!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1334cc4ba5770c33%3A0x466c9f1947c0f0a0!2sUlica%20Zdeslava%20Turi%C4%87a%207%2C%2023211%2C%20Drage!5e0!3m2!1shr!2shr!4v1775249672872!5m2!1shr!2shr"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Apartments Rosanda Location"
                ></iframe>
                <a
                  href="https://maps.app.goo.gl/36eTVUbCcC9Hx6gi8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 left-2 bg-white/90 backdrop-blur-md px-4 py-2  flex items-center gap-3 shadow-lg cursor-pointer hover:scale-105 transition-transform z-10"
                >
                  <span className="material-symbols-outlined material-symbols-rounded text-secondary text-base block leading-none">explore</span>
                  <span className="font-label text-xs font-bold uppercase tracking-widest text-primary">{t('contactPage.openMaps')}</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ContactPage; 
